import { useRef, useState, useEffect } from 'react';
import { categoriesBySection } from '@/mocks/catalog';

interface CategoryQuickNavProps {
  activeSection: string;
  activeCategories: string[];
  onSectionChange: (section: string) => void;
  onCategoryToggle: (category: string) => void;
  onClearCategories: () => void;
  onClearAll: () => void;
  totalCount: number;
}

const SECTION_CONFIG: Record<string, { icon: string; color: string; glow: string; bg: string }> = {
  'Лекарства':            { icon: 'ri-capsule-line',        color: '#ff2d78', glow: 'rgba(255,45,120,0.35)',  bg: 'rgba(255,45,120,0.1)' },
  'БАДы и витамины':      { icon: 'ri-sun-line',            color: '#f0ff00', glow: 'rgba(240,255,0,0.35)',   bg: 'rgba(240,255,0,0.1)' },
  'Дермакосметика':       { icon: 'ri-sparkling-2-line',    color: '#ff6b9d', glow: 'rgba(255,107,157,0.35)', bg: 'rgba(255,107,157,0.1)' },
  'Гомеопатия':           { icon: 'ri-leaf-line',           color: '#39ff14', glow: 'rgba(57,255,20,0.35)',   bg: 'rgba(57,255,20,0.1)' },
  'Контактные линзы':     { icon: 'ri-eye-line',            color: '#00f5ff', glow: 'rgba(0,245,255,0.35)',   bg: 'rgba(0,245,255,0.1)' },
  'Медицинские изделия':  { icon: 'ri-first-aid-kit-line',  color: '#ff6b00', glow: 'rgba(255,107,0,0.35)',   bg: 'rgba(255,107,0,0.1)' },
  'Медицинские приборы':  { icon: 'ri-heart-pulse-line',    color: '#c77dff', glow: 'rgba(199,125,255,0.35)', bg: 'rgba(199,125,255,0.1)' },
};

const CATEGORY_COLORS: Record<string, string> = {
  'Сердце и сосуды': '#ff2d78', 'Обезболивающие': '#00f5ff', 'Желудок и кишечник': '#39ff14',
  'Простуда и грипп': '#00f5ff', 'Антибиотики': '#ff6b00', 'Аллергия': '#c77dff', 'Нейропротекторы': '#c77dff',
  'Витамины': '#f0ff00', 'Мультивитамины': '#f0ff00', 'Минералы': '#00f5ff', 'Омега и жирные кислоты': '#39ff14',
  'Антиоксиданты': '#39ff14', 'Пробиотики': '#00f5ff', 'Красота и здоровье': '#ff6b9d', 'Спортивное питание': '#ff6b00',
  'Уход за лицом': '#ff6b9d', 'Уход за телом': '#ff6b9d', 'Уход за волосами': '#ff6b9d',
  'Иммунитет': '#39ff14', 'Нервная система': '#c77dff', 'Суставы и мышцы': '#ff6b00',
  'Однодневные линзы': '#00f5ff', 'Месячные линзы': '#00f5ff', 'Растворы и уход': '#00f5ff',
  'Перевязочные материалы': '#ff6b00', 'Тонометры': '#ff6b00', 'Термометры': '#ff6b00', 'Средства защиты': '#ff6b00',
  'Ингаляторы': '#c77dff', 'Глюкометры': '#c77dff', 'Пульсоксиметры': '#c77dff', 'Массажёры': '#c77dff',
};

const ScrollRow = ({ children }: { children: React.ReactNode }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const check = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    check();
    const el = scrollRef.current;
    if (el) el.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    return () => {
      if (el) el.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, []);

  const scroll = (dir: 'left' | 'right') => {
    scrollRef.current?.scrollBy({ left: dir === 'left' ? -220 : 220, behavior: 'smooth' });
  };

  return (
    <div className="relative">
      {canLeft && (
        <>
          <div className="absolute left-0 top-0 bottom-0 w-12 z-10 pointer-events-none" style={{ background: 'linear-gradient(to right, #050508, transparent)' }} />
          <button onClick={() => scroll('left')} className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-7 h-7 flex items-center justify-center rounded-full cursor-pointer" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.5)' }}>
            <i className="ri-arrow-left-s-line text-sm" />
          </button>
        </>
      )}
      {canRight && (
        <>
          <div className="absolute right-0 top-0 bottom-0 w-12 z-10 pointer-events-none" style={{ background: 'linear-gradient(to left, #050508, transparent)' }} />
          <button onClick={() => scroll('right')} className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-7 h-7 flex items-center justify-center rounded-full cursor-pointer" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.5)' }}>
            <i className="ri-arrow-right-s-line text-sm" />
          </button>
        </>
      )}
      <div ref={scrollRef} className="flex items-center gap-2 overflow-x-auto px-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {children}
      </div>
    </div>
  );
};

const CategoryQuickNav = ({
  activeSection,
  activeCategories,
  onSectionChange,
  onCategoryToggle,
  onClearCategories,
  onClearAll,
  totalCount,
}: CategoryQuickNavProps) => {
  const sections = Object.keys(SECTION_CONFIG);
  const subcategories = activeSection ? (categoriesBySection[activeSection] ?? []) : [];

  return (
    <div className="mb-5 space-y-2.5">
      {/* ── Row 1: Sections ── */}
      <ScrollRow>
        {/* All */}
        <button
          onClick={onClearAll}
          className="whitespace-nowrap flex items-center gap-2 px-4 py-2.5 rounded-full font-mono text-xs cursor-pointer transition-all duration-300 shrink-0"
          style={
            !activeSection
              ? { background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', boxShadow: '0 0 12px rgba(255,255,255,0.1)' }
              : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.4)' }
          }
        >
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-apps-2-line text-sm" /></div>
          Все разделы
          {!activeSection && (
            <span className="px-1.5 py-0.5 rounded-full font-mono text-[9px] font-bold" style={{ background: 'rgba(255,255,255,0.15)', color: '#fff' }}>
              {totalCount}
            </span>
          )}
        </button>

        <div className="w-px h-6 shrink-0" style={{ background: 'rgba(255,255,255,0.08)' }} />

        {sections.map((sec) => {
          const cfg = SECTION_CONFIG[sec];
          const isActive = activeSection === sec;
          return (
            <button
              key={sec}
              onClick={() => onSectionChange(sec)}
              className="whitespace-nowrap flex items-center gap-2 px-4 py-2.5 rounded-full font-mono text-xs cursor-pointer transition-all duration-300 shrink-0 relative overflow-hidden"
              style={
                isActive
                  ? { background: cfg.bg, border: `1px solid ${cfg.color}`, color: cfg.color, boxShadow: `0 0 14px ${cfg.glow}` }
                  : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.45)' }
              }
            >
              {isActive && <span className="absolute inset-0 rounded-full opacity-20 animate-pulse" style={{ background: cfg.color }} />}
              <div className="w-4 h-4 flex items-center justify-center relative z-10">
                <i className={`${cfg.icon} text-sm`} />
              </div>
              <span className="relative z-10">{sec}</span>
              {isActive && (
                <span className="relative z-10 w-4 h-4 flex items-center justify-center rounded-full text-[9px] font-bold" style={{ background: cfg.color, color: '#050508' }}>
                  <i className="ri-check-line" />
                </span>
              )}
            </button>
          );
        })}
      </ScrollRow>

      {/* ── Row 2: Subcategories (only when section selected) ── */}
      {activeSection && subcategories.length > 0 && (
        <div
          className="rounded-xl px-3 py-2.5"
          style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
        >
          <ScrollRow>
            {/* All in section */}
            <button
              onClick={onClearCategories}
              className="whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-[11px] cursor-pointer transition-all duration-200 shrink-0"
              style={
                activeCategories.length === 0
                  ? { background: `${SECTION_CONFIG[activeSection].color}18`, border: `1px solid ${SECTION_CONFIG[activeSection].color}50`, color: SECTION_CONFIG[activeSection].color }
                  : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.4)' }
              }
            >
              <i className="ri-list-check text-xs" />
              Все подкатегории
            </button>

            <div className="w-px h-5 shrink-0" style={{ background: 'rgba(255,255,255,0.06)' }} />

            {subcategories.map((cat) => {
              const isActive = activeCategories.includes(cat);
              const catColor = CATEGORY_COLORS[cat] ?? SECTION_CONFIG[activeSection].color;
              return (
                <button
                  key={cat}
                  onClick={() => onCategoryToggle(cat)}
                  className="whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-[11px] cursor-pointer transition-all duration-200 shrink-0"
                  style={
                    isActive
                      ? { background: `${catColor}15`, border: `1px solid ${catColor}60`, color: catColor, boxShadow: `0 0 8px ${catColor}25` }
                      : { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.4)' }
                  }
                >
                  {isActive && <i className="ri-check-line text-[10px]" />}
                  {cat}
                </button>
              );
            })}
          </ScrollRow>
        </div>
      )}
    </div>
  );
};

export default CategoryQuickNav;
